/*
 * firebase/messaging is fetched when a token is wanted, not with the app: it
 * is large, and nothing on the way to the first screen needs it.
 */
const messaging = () => import('firebase/messaging');
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { APP_NAME, COL } from '../../shared/constants';
import { tell } from './errors';
import { app, db } from './firebase';

export type InstallState = 'installed' | 'needs-install' | 'unsupported';

/**
 * On iOS, web push only exists for a PWA that has been added to the Home
 * Screen (16.4+). Asking for permission in Safari there fails in a way the
 * user cannot recover from, so we check first and show instructions instead.
 */
export function installState(): InstallState {
  const standalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as { standalone?: boolean }).standalone === true;

  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return standalone ? 'unsupported' : 'needs-install';
  }
  return standalone ? 'installed' : 'needs-install';
}

export function isIos(): boolean {
  return /iP(hone|ad|od)/.test(navigator.userAgent);
}

/**
 * Chrome and Edge fire beforeinstallprompt once, early, and only then will
 * they let us open the real install dialog. Miss that event and the only
 * remaining option is telling people to hunt through a browser menu, so it is
 * captured at module load rather than when the install screen mounts.
 *
 * Safari never fires it. iOS keeps the Share-sheet instructions.
 */
interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferredPrompt: InstallPromptEvent | null = null;
const promptListeners = new Set<() => void>();

function announce(): void {
  for (const listener of promptListeners) listener();
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Without this the browser shows its own mini-infobar instead of letting
    // the install screen ask at the moment it explains why.
    event.preventDefault();
    deferredPrompt = event as InstallPromptEvent;
    announce();
  });

  window.addEventListener('appinstalled', () => {
    // A used prompt cannot be reused, and the install is done regardless.
    deferredPrompt = null;
    announce();
  });
}

export function canPromptInstall(): boolean {
  return deferredPrompt !== null;
}

/** Subscribes to prompt availability changing. Returns an unsubscribe. */
export function onInstallAvailabilityChange(listener: () => void): () => void {
  promptListeners.add(listener);
  return () => promptListeners.delete(listener);
}

/**
 * Opens the browser's install dialog. Resolves true only once the app is
 * actually installed, so the caller can advance.
 */
export async function promptInstall(): Promise<boolean> {
  const event = deferredPrompt;
  if (!event) return false;

  // Single-use: the browser refuses a second prompt() on the same event.
  deferredPrompt = null;
  announce();

  await event.prompt();
  const { outcome } = await event.userChoice;
  return outcome === 'accepted';
}

/**
 * How turning notifications on went, so the switch can say why it is still
 * off. It used to answer yes or no, and every no -- not on the Home Screen,
 * turned down, the phone refusing to sign up for pushes -- left the switch
 * sitting there as though it had not been pressed.
 */
export type PushResult = 'on' | 'needs-install' | 'blocked' | 'refused' | 'failed';

/**
 * Must be called from a user gesture. Browsers ignore permission requests that
 * are not, and iOS holds it against you.
 */
export async function enableNotifications(uid: string): Promise<PushResult> {
  if (installState() !== 'installed' && isIos()) return 'needs-install';

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return 'blocked';

  try {
    const registration = await navigator.serviceWorker.ready;
    const { getMessaging, getToken } = await messaging();
    const token = await getToken(getMessaging(app), {
      vapidKey: import.meta.env.VITE_FCM_VAPID_KEY,
      serviceWorkerRegistration: registration,
    });
    if (!token) return 'failed';
    await saveToken(uid, token);
    return 'on';
  } catch (err) {
    // Still recorded, as it was when nothing caught it.
    void tell(uid, err);
    return refusedByPhone(err) ? 'refused' : 'failed';
  }
}

/**
 * Allowed to show notifications, and then refused the push subscription
 * itself. iOS does this -- "User denied push permission" -- when Givi Den is
 * switched off in its Settings, even with the permission granted.
 */
export function refusedByPhone(err: unknown): boolean {
  const { name, code, message } = (err ?? {}) as { name?: string; code?: string; message?: string };
  return name === 'NotAllowedError' || /permission/i.test(code ?? '') || /denied|permission/i.test(message ?? '');
}

/** Where to go to allow them, which is the phone's settings once it has said no. */
export function howToAllow(ios: boolean, installed: boolean): string {
  if (ios) return `open Settings, tap Notifications, find ${APP_NAME}, and turn on Allow Notifications.`;
  if (installed) return `press and hold the ${APP_NAME} icon, tap App info, then Notifications, and turn them on.`;
  return 'in Chrome, tap the icon left of the address, then Permissions, and turn on Notifications.';
}

/** What to say under the switch when it did not turn on; null when it did. */
export function pushProblem(result: PushResult, ios: boolean, installed: boolean): string | null {
  switch (result) {
    case 'on':
      return null;
    case 'needs-install':
      return `Open ${APP_NAME} from your Home Screen first. iPhone only allows notifications there.`;
    case 'blocked':
      return `Notifications are turned off for ${APP_NAME}. To turn them on: ${howToAllow(ios, installed)}`;
    case 'refused':
      return `Your phone would not let ${APP_NAME} send notifications. To allow them: ${howToAllow(ios, installed)}`;
    case 'failed':
      return 'Could not turn notifications on. Check your signal and try again.';
  }
}

/**
 * This browser, across however many tokens FCM issues it.
 *
 * The row used to be keyed by the token, on the reasoning that re-registering
 * the same device could not duplicate it. True of the same token — but FCM
 * rotates them, and a rotation wrote a second row while the first stayed
 * behind with a subscription nobody had unsubscribed. Every push then went to
 * both, which is one notification from the installed app and one from the
 * browser, for the same comment.
 */
const INSTALL_KEY = 'givisnap:installId';

function installId(): string {
  try {
    const found = localStorage.getItem(INSTALL_KEY);
    if (found) return found;
    const made = crypto.randomUUID();
    localStorage.setItem(INSTALL_KEY, made);
    return made;
  } catch {
    // No storage to remember it in. One row per session is still better than
    // one per token, and this browser was never going to keep a push
    // subscription across launches anyway.
    return crypto.randomUUID();
  }
}

async function saveToken(uid: string, token: string): Promise<void> {
  const id = installId();
  const platform = navigator.userAgent.slice(0, 120);

  await setDoc(doc(db, COL.users, uid, 'tokens', id), {
    token,
    platform,
    installId: id,
    updatedAt: serverTimestamp(),
  });

  /*
   * Clear what this browser left behind under the old scheme. A row with no
   * installId was written by a version that keyed on the token, and one of
   * those carrying this exact user agent is this browser's own earlier
   * registration — the duplicate. Rows from other devices have a different
   * agent and are not touched.
   */
  const rows = await getDocs(collection(db, COL.users, uid, 'tokens'));
  await Promise.all(
    rows.docs
      .filter((row) => row.id !== id && !row.get('installId') && row.get('platform') === platform)
      .map((row) => deleteDoc(row.ref)),
  );
}

/**
 * Called on every launch once membership is known, so the row stays pointed at
 * whatever token FCM has issued this browser lately rather than only at the
 * one it had on the day permission was granted.
 */
export async function refreshPushToken(uid: string): Promise<void> {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  if (!('serviceWorker' in navigator)) return;

  try {
    const registration = await navigator.serviceWorker.ready;
    const { getMessaging, getToken } = await messaging();
    const token = await getToken(getMessaging(app), {
      vapidKey: import.meta.env.VITE_FCM_VAPID_KEY,
      serviceWorkerRegistration: registration,
    });
    if (!token || !tokenChanged(readSavedToken(), uid, token)) return;
    await saveToken(uid, token);
    writeSavedToken(uid, token);
  } catch {
    // Offline, or push unavailable. The row it already has still works.
  }
}

/*
 * The token last written for this install, so an unchanged one is not written
 * again -- and every token row read back -- on every opening of the app. FCM
 * is still asked each time: that is how a rotated token is noticed at all.
 */
const SAVED_TOKEN_KEY = 'givisnap:pushTokenSaved';

export function tokenChanged(saved: string | null, uid: string, token: string): boolean {
  return saved !== `${uid} ${token}`;
}

function readSavedToken(): string | null {
  try {
    return localStorage.getItem(SAVED_TOKEN_KEY);
  } catch {
    return null;
  }
}

function writeSavedToken(uid: string, token: string): void {
  try {
    localStorage.setItem(SAVED_TOKEN_KEY, `${uid} ${token}`);
  } catch {
    // Blocked storage: it is written again next time, as before.
  }
}

export async function disableNotifications(): Promise<void> {
  await messaging()
    .then(({ deleteToken, getMessaging }) => deleteToken(getMessaging(app)))
    .catch(() => undefined);
  // Turned back on later, the token is written whatever it is.
  try {
    localStorage.removeItem(SAVED_TOKEN_KEY);
  } catch {
    // Nothing kept to forget.
  }
}

export async function registerServiceWorker(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  await navigator.serviceWorker.register('/sw.js', { type: 'module' });
}
